/**
 * Edits a draft rules version in place: section titles and summaries; rules (text with {name}
 * placeholders, parameters, the programs they are about); table cells and rows. Each rule previews the
 * sentence the page will show. Saving replaces the draft (UpdateRuleDraft) with the updatedAt it was
 * opened at, so a concurrent edit is reported instead of overwritten.
 */
import { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  ColumnLayout,
  Container,
  FormField,
  Header,
  Input,
  Multiselect,
  Select,
  SpaceBetween,
  Textarea,
} from '@amzn/awsui-components-react';
import { useTranslation } from 'react-i18next';

import { getCpaClient, type RuleVersionData, UpdateRuleDraftCommand } from '../../client/CpaClient';
import {
  allRuleIds,
  type EditableParam,
  type EditableRule,
  type EditableSection,
  fromEditable,
  newId,
  nextKey,
  toEditable,
  validateEditable,
} from '../../utils/ruleEditor';
import { workflowErrorOf } from '../../utils/ruleVersions';
import { usePrograms } from '../../state/usePrograms';

const FIELDS = ['qualifier_min', 'qualifier_max'] as const;

/** p1, p2, ... — the first not already a parameter name (names must be letters and digits). */
function nextParamName(taken: string[]): string {
  for (let i = 1; ; i++) if (!taken.includes(`p${i}`)) return `p${i}`;
}

/** The sentence as the page will show it; program parameters show where their value comes from. */
function Preview({ rule, fieldLabel }: { rule: EditableRule; fieldLabel: (f: string) => string }) {
  const parts = rule.text.split(/(\{[^{}]*\})/);
  return (
    <Box variant="small" color="text-body-secondary">
      {parts.map((chunk, i) => {
        const m = /^\{([^{}]*)\}$/.exec(chunk);
        const p = m && rule.params.find((x) => x.name.trim() === m[1]);
        if (!m) return <span key={i}>{chunk}</span>;
        if (!p)
          return (
            <span key={i} className="cpa-rules-param is-missing">
              {chunk}
            </span>
          );
        return (
          <span key={i} className={`cpa-rules-param ${p.kind === 'program' ? 'is-live' : ''}`}>
            {p.kind === 'program' ? `${p.program || '?'} · ${fieldLabel(p.field)}` : p.value || '?'}
          </span>
        );
      })}
    </Box>
  );
}

export default function RuleEditor({
  rules,
  onCancel,
  onSaved,
}: {
  rules: RuleVersionData;
  onCancel: () => void;
  onSaved: (saved: RuleVersionData) => void;
}) {
  const { t } = useTranslation('cpa');
  const tx = t as unknown as (key: string) => string;
  const [sections, setSections] = useState<EditableSection[]>(() => toEditable(rules.sections));
  const [note, setNote] = useState(rules.note ?? '');
  const [shownErrors, setShownErrors] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<{ key: string; detail?: string } | null>(null);

  const errors = useMemo(() => validateEditable(sections), [sections]);
  // Existing programs, for program tags and program parameters.
  const { state: programsState, reload: reloadPrograms } = usePrograms();
  const programOptions = (programsState.kind === 'ready' ? programsState.programs : [])
    .map((p) => p.name)
    .filter((n): n is string => !!n)
    .sort()
    .map((n) => ({ value: n, label: n }));
  // Program pickers say when the list is still loading or could not be loaded, rather than look empty.
  const pickerStatus = {
    statusType: (programsState.kind === 'loading'
      ? 'loading'
      : programsState.kind === 'ready'
        ? 'finished'
        : 'error') as 'loading' | 'finished' | 'error',
    loadingText: t('editRuleProgramsLoading'),
    errorText: t('editRuleProgramsError'),
  };
  const fieldLabel = (f: string) => tx(`editRuleField_${f}`);

  const updateSection = (si: number, change: Partial<EditableSection>) =>
    setSections((all) => all.map((s, i) => (i === si ? { ...s, ...change } : s)));
  const updateRule = (si: number, ri: number, change: Partial<EditableRule>) =>
    setSections((all) =>
      all.map((s, i) =>
        i === si ? { ...s, rules: s.rules.map((r, j) => (j === ri ? { ...r, ...change } : r)) } : s,
      ),
    );
  const updateParam = (si: number, ri: number, pi: number, change: Partial<EditableParam>) =>
    updateRuleWith(si, ri, (r) => ({ params: r.params.map((p, k) => (k === pi ? { ...p, ...change } : p)) }));
  /** Like updateRule, but derives the change from the latest rule (new ids and names must). */
  const updateRuleWith = (si: number, ri: number, change: (r: EditableRule) => Partial<EditableRule>) =>
    setSections((all) =>
      all.map((s, i) =>
        i === si ? { ...s, rules: s.rules.map((r, j) => (j === ri ? { ...r, ...change(r) } : r)) } : s,
      ),
    );
  // New ids and names come from the latest state inside the updater, so two quick adds cannot collide.
  const addRule = (si: number) =>
    setSections((all) =>
      all.map((s, i) =>
        i === si
          ? {
              ...s,
              rules: [
                ...s.rules,
                { ruleId: newId('rule', allRuleIds(all)), text: '', params: [], programs: [] },
              ],
            }
          : s,
      ),
    );
  const addParam = (si: number, ri: number) =>
    updateRuleWith(si, ri, (r) => ({
      params: [
        ...r.params,
        {
          key: nextKey(),
          name: nextParamName(r.params.map((x) => x.name)),
          kind: 'engine',
          value: '',
          program: '',
          field: 'qualifier_min',
        },
      ],
    }));

  const save = () => {
    setShownErrors(true);
    if (Object.keys(errors).length > 0) return;
    setSaving(true);
    setSaveError(null);
    getCpaClient()
      .send(
        new UpdateRuleDraftCommand({
          version: rules.version,
          sections: fromEditable(sections),
          note: note.trim() || undefined,
          expectedUpdatedAt: rules.updatedAt,
        }),
      )
      .then((out) => onSaved(out.rules!))
      .catch((e: unknown) => setSaveError(workflowErrorOf(e)))
      .finally(() => setSaving(false));
  };

  const errorText = (at: string) =>
    shownErrors && errors[at] ? errors[at].map((k) => tx(k)).join('；') : undefined;

  return (
    <SpaceBetween size="l">
      <Container
        header={
          <Header
            variant="h2"
            description={t('editRulesCaption')}
            actions={
              <SpaceBetween direction="horizontal" size="xs">
                <Button variant="link" onClick={onCancel} disabled={saving}>
                  {t('cancel')}
                </Button>
                <Button variant="primary" loading={saving} onClick={save}>
                  {t('editRulesSave')}
                </Button>
              </SpaceBetween>
            }
          >
            {t('editRulesTitle', { version: rules.version })}
          </Header>
        }
      >
        <SpaceBetween size="s">
          <FormField label={t('editRulesNote')} constraintText={t('editRulesNoteHint')}>
            <Input value={note} onChange={({ detail }) => setNote(detail.value)} />
          </FormField>
          {programsState.kind === 'error' && (
            <Alert type="warning" action={<Button onClick={reloadPrograms}>{t('programsRetry')}</Button>}>
              {t('editRuleProgramsError')}
            </Alert>
          )}
          {/* A denial is a permission problem: retrying cannot help. */}
          {programsState.kind === 'denied' && <Alert type="warning">{t('programsDenied')}</Alert>}
          {shownErrors && Object.keys(errors).length > 0 && <Alert type="error">{t('editRulesFix')}</Alert>}
          {saveError && (
            <Alert type="error" header={tx(saveError.key)}>
              {saveError.detail}
            </Alert>
          )}
        </SpaceBetween>
      </Container>

      {sections.map((s, si) => (
        <Container
          key={s.sectionId}
          header={
            <Header
              variant="h3"
              actions={
                <Button
                  iconName="remove"
                  variant="icon"
                  ariaLabel={t('editRuleRemoveSection')}
                  onClick={() => setSections((all) => all.filter((_, i) => i !== si))}
                />
              }
            >
              {`${si + 1}. ${s.title || t('editRuleUntitled')}`}
            </Header>
          }
        >
          <SpaceBetween size="m">
            <ColumnLayout columns={2}>
              <FormField label={t('editRuleSectionTitle')} errorText={errorText(`s:${s.sectionId}`)}>
                <Input
                  value={s.title}
                  onChange={({ detail }) => updateSection(si, { title: detail.value })}
                />
              </FormField>
              <FormField label={t('editRuleSectionSummary')}>
                <Input
                  value={s.summary}
                  onChange={({ detail }) => updateSection(si, { summary: detail.value })}
                />
              </FormField>
            </ColumnLayout>

            {s.rules.map((r, ri) => (
              <div key={r.ruleId} className="cpa-rules-edit-rule">
                <SpaceBetween size="xs">
                  <FormField
                    label={`${si + 1}.${ri + 1}`}
                    errorText={errorText(`r:${r.ruleId}`)}
                    constraintText={t('editRuleTextHint')}
                    secondaryControl={
                      <Button
                        iconName="remove"
                        variant="icon"
                        ariaLabel={t('editRuleRemove')}
                        onClick={() => updateSection(si, { rules: s.rules.filter((_, j) => j !== ri) })}
                      />
                    }
                  >
                    <Textarea
                      rows={2}
                      value={r.text}
                      onChange={({ detail }) => updateRule(si, ri, { text: detail.value })}
                    />
                  </FormField>
                  <Preview rule={r} fieldLabel={fieldLabel} />
                  {r.params.map((p, pi) => (
                    <ColumnLayout key={p.key} columns={4}>
                      <Input
                        ariaLabel={t('editRuleParamNameLabel')}
                        placeholder={t('editRuleParamNameLabel')}
                        value={p.name}
                        onChange={({ detail }) => updateParam(si, ri, pi, { name: detail.value })}
                      />
                      <Select
                        ariaLabel={t('editRuleParamKind')}
                        selectedOption={{ value: p.kind, label: tx(`editRuleKind_${p.kind}`) }}
                        options={['engine', 'program'].map((k) => ({
                          value: k,
                          label: tx(`editRuleKind_${k}`),
                        }))}
                        onChange={({ detail }) =>
                          updateParam(si, ri, pi, {
                            kind: detail.selectedOption.value as EditableParam['kind'],
                          })
                        }
                      />
                      {p.kind === 'engine' ? (
                        <Input
                          ariaLabel={t('editRuleParamValue')}
                          placeholder={t('editRuleParamValue')}
                          value={p.value}
                          onChange={({ detail }) => updateParam(si, ri, pi, { value: detail.value })}
                        />
                      ) : (
                        <SpaceBetween direction="horizontal" size="xxs">
                          <Select
                            ariaLabel={t('editRuleParamProgram')}
                            placeholder={t('editRuleParamProgram')}
                            filteringType="auto"
                            selectedOption={p.program ? { value: p.program, label: p.program } : null}
                            options={programOptions}
                            {...pickerStatus}
                            onChange={({ detail }) =>
                              updateParam(si, ri, pi, { program: detail.selectedOption.value! })
                            }
                          />
                          <Select
                            ariaLabel={t('editRuleParamField')}
                            selectedOption={{ value: p.field, label: fieldLabel(p.field) }}
                            options={FIELDS.map((f) => ({ value: f, label: fieldLabel(f) }))}
                            onChange={({ detail }) =>
                              updateParam(si, ri, pi, { field: detail.selectedOption.value! })
                            }
                          />
                        </SpaceBetween>
                      )}
                      <Button
                        iconName="remove"
                        variant="icon"
                        ariaLabel={t('editRuleRemoveParam')}
                        onClick={() => updateRule(si, ri, { params: r.params.filter((_, k) => k !== pi) })}
                      />
                    </ColumnLayout>
                  ))}
                  <SpaceBetween direction="horizontal" size="xs" alignItems="center">
                    <Button iconName="add-plus" variant="inline-link" onClick={() => addParam(si, ri)}>
                      {t('editRuleAddParam')}
                    </Button>
                  </SpaceBetween>
                  <FormField label={t('editRulePrograms')}>
                    <Multiselect
                      filteringType="auto"
                      selectedOptions={r.programs.map((p) => ({ value: p, label: p }))}
                      options={programOptions}
                      {...pickerStatus}
                      onChange={({ detail }) =>
                        updateRule(si, ri, { programs: detail.selectedOptions.map((o) => o.value!) })
                      }
                    />
                  </FormField>
                </SpaceBetween>
              </div>
            ))}

            <Button iconName="add-plus" onClick={() => addRule(si)}>
              {t('editRuleAdd')}
            </Button>

            {s.table && (
              <FormField label={t('editRuleTable')}>
                <SpaceBetween size="xxs">
                  {s.table.rows.map((row, rowi) => (
                    <ColumnLayout key={row.key} columns={Math.min(4, s.table!.columns.length + 1)}>
                      {s.table!.columns.map((c) => (
                        <Input
                          key={c.columnId}
                          ariaLabel={c.title}
                          placeholder={c.title}
                          value={row.cells[c.columnId] ?? ''}
                          onChange={({ detail }) =>
                            updateSection(si, {
                              table: {
                                ...s.table!,
                                rows: s.table!.rows.map((x, k) =>
                                  k === rowi
                                    ? { ...x, cells: { ...x.cells, [c.columnId]: detail.value } }
                                    : x,
                                ),
                              },
                            })
                          }
                        />
                      ))}
                      <Button
                        iconName="remove"
                        variant="icon"
                        ariaLabel={t('editRuleRemoveRow')}
                        onClick={() =>
                          updateSection(si, {
                            table: { ...s.table!, rows: s.table!.rows.filter((_, k) => k !== rowi) },
                          })
                        }
                      />
                    </ColumnLayout>
                  ))}
                  <Button
                    iconName="add-plus"
                    variant="inline-link"
                    onClick={() =>
                      updateSection(si, {
                        table: { ...s.table!, rows: [...s.table!.rows, { key: nextKey(), cells: {} }] },
                      })
                    }
                  >
                    {t('editRuleAddRow')}
                  </Button>
                </SpaceBetween>
              </FormField>
            )}
          </SpaceBetween>
        </Container>
      ))}

      <Button
        iconName="add-plus"
        onClick={() =>
          setSections((all) => [
            ...all,
            {
              sectionId: newId(
                'section',
                all.map((s) => s.sectionId),
              ),
              title: '',
              summary: '',
              rules: [],
            },
          ])
        }
      >
        {t('editRuleAddSection')}
      </Button>
    </SpaceBetween>
  );
}
